import React, { memo, useCallback, useMemo, useState } from 'react';

import { Confirm, Modal, Table } from '@components/common';

import { useMutateUpdateOrganisationSettings } from '@hooks/organisation/useMutateUpdateOrganisationSettings';
import { useQueryOrganisationSettings } from '@hooks/organisation/useQueryOrganisationSettings';
import { useQueryOrganisationSettingsVendors } from '@hooks/organisation/useQueryOrganisationSettingsVendors';

import { organisationSettings } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setPage, setPageSize, setSort } from '@slices/organisationSettings';

import { ISortBy, ITablePagination, ITableSort } from '@interfaces/components/table';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { getConfirmChangeStatusText, IDataForEditInterface } from './constants';

import EditSettingsForm from './EditSettingsForm';
import { getColumns } from './manageSettingsColumns';

import styles from './Settings.module.scss';

interface ISettingsInterface {
	organisationId?: string;
}

const Settings = ({
	organisationId,
}: ISettingsInterface) => {
	const dispatch = useAppDispatch();

	const [isEditModalShown, setIsEditModalShown] = useState(false);
	const [isConfirmModalShown, setIsConfirmModalShown] = useState(false);
	const [isConfirmChangeStatusModal, setIsConfirmChangeStatusModal] = useState(false);
	const [dataForEdit, setDataForEdit] = useState<IDataForEditInterface | null>(null);

	const openConfirmModal = useCallback(() => setIsConfirmModalShown(true), []);
	const closeConfirmModal = () => setIsConfirmModalShown(false);

	const openConfirmChangeStatusModal = useCallback(() => setIsConfirmChangeStatusModal(true), []);
	const closeConfirmChangeStatusModal = () => setIsConfirmChangeStatusModal(false);

	const openEditModal = () => setIsEditModalShown(true);
	const closeEditModal = useCallback(() => setIsEditModalShown(false), []);

	const onConfirm = useCallback(() => {
		closeEditModal();

		closeConfirmModal();

		setDataForEdit(null);
	}, [closeEditModal]);

	const {
		page,
		pageSize,
		sortFieldName,
		sortDirection,
	} = useAppSelector(organisationSettings);

	const {
		organizationSettings,
		countOfRecords,
		isLoadingOrganisationSettings,
	} = useQueryOrganisationSettings(organisationId);

	const {
		organizationSettingsVendors,
		isLoadingOrganisationSettingsVendors,
	} = useQueryOrganisationSettingsVendors();

	const { handleUpdateOrganisationSettings } = useMutateUpdateOrganisationSettings();

	const columns = useMemo(() => getColumns({
		handleUpdateOrganisationSettings,
		openEditModal,
		setDataForEdit,
		openConfirmChangeStatusModal,
		organisationId,
	}), [
		openConfirmChangeStatusModal,
		handleUpdateOrganisationSettings,
		organisationId,
	]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const onConfirmChangeStatus = useCallback(() => {
		if (!dataForEdit || !organisationId) return;

		closeConfirmChangeStatusModal();

		handleUpdateOrganisationSettings({
			vendorsSettings: [{
				...dataForEdit,
				vendor: dataForEdit.vendor.name,
			}],
			organisationId,
		});

		setDataForEdit(null);
	}, [
		dataForEdit,
		handleUpdateOrganisationSettings,
		organisationId,
	]);

	return (
		<div className={styles.settingsContainer}>
			<Table
				columns={columns}
				data={organizationSettings}
				emptyText='No data available'
				isEmptyTextCenter
				isLoading={isLoadingOrganisationSettings || isLoadingOrganisationSettingsVendors}
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight='55vh'
				pagination={pagination}
				sort={sort}
			/>
			{dataForEdit && organizationSettingsVendors && (
				<Modal
					isOpen={isEditModalShown && !!dataForEdit}
					onClose={openConfirmModal}
					title='Edit settings'
					width='470px'
				>
					<EditSettingsForm
						closeEditModal={closeEditModal}
						dataForEdit={dataForEdit}
						handleUpdateOrganisationSettings={handleUpdateOrganisationSettings}
						onClose={openConfirmModal}
						organisationId={organisationId}
						organizationSettingsVendors={organizationSettingsVendors}
					/>
				</Modal>
			)}
			<Modal
				isOpen={isConfirmModalShown}
				onClose={closeConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={onConfirm}
					question='Are you sure you want to close the form without saving?'
				>
					Your current changes will be lost.
				</Confirm>
			</Modal>
			<Modal
				isOpen={isConfirmChangeStatusModal && !!dataForEdit}
				onClose={closeConfirmChangeStatusModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={onConfirmChangeStatus}
					question={getConfirmChangeStatusText(dataForEdit?.vendor?.title as string)}
				/>
			</Modal>
		</div>
	);
};

export default memo(Settings);
